/* ─── Shared widget config shapes ──────────────────────────── */

export type DayKey =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export interface TimePeriod {
  open: string; // HH:MM
  close: string; // HH:MM
}

export interface RecurringRule {
  startDate: string; // YYYY-MM-DD
  periods: TimePeriod[];
  exceptLastSaturdayOfMonth?: boolean;
}

export interface DayHours {
  day: string; // "Montag"
  dayShort: string; // "Mo"
  periods?: TimePeriod[];
  closed?: boolean;
  recurringRule?: RecurringRule;
}

export interface SpecialClosure {
  name: string;
  startDate: string;
  endDate: string;
  reason?: string;
}

export type OpeningHoursConfig = {
  /** Optional heading override. Defaults to "Öffnungszeiten" when empty. */
  title?: string;
  /** Optional subtitle override. Defaults to "Wir sind für Sie erreichbar" when empty. */
  subtitle?: string;
  regularHours: {
    [K in DayKey]?: DayHours;
  };
  specialClosures: SpecialClosure[];
};

export type SocialPlatform = 'instagram' | 'facebook';

/** Einstellungen für die Hero-Karussell-Darstellung des Feeds. */
export interface HeroCarouselConfig {
  /** Das im Template hinterlegte Hero-Bild als ersten Slide voranstellen. */
  useHeroImageAsFirstSlide: boolean;
  /** Optionaler expliziter Hero-Bild-URL/-Pfad. Leer = Template-Hero-Bild. */
  heroImageUrl?: string;
  /** Automatisch zum nächsten Slide weiterschalten. */
  autoplay: boolean;
  /** Sekunden pro Slide bei Autoplay (2–30). */
  intervalSeconds: number;
  /** Bildunterschriften (Captions) der Posts einblenden. */
  showCaptions: boolean;
}

export interface SocialMediaFeedConfig {
  platform: SocialPlatform;
  handle: string;
  count: number;
  refreshIntervalMinutes?: number;
  /** Social-Media-Posts im Hero-Karussell anzeigen. */
  showInHero: boolean;
  /** Social-Media-Posts als Raster im News-Bereich anzeigen. */
  showAsGrid: boolean;
  /** Einstellungen für das Hero-Karussell. */
  heroCarousel?: HeroCarouselConfig;
}

export type WawiProductsLayout = 'grid' | 'list';

export interface WawiProductsConfig {
  title?: string;
  subtitle?: string;
  maxProducts: number;
  layout: WawiProductsLayout;
  showPrice: boolean;
  search?: string;
}

export interface WawiCoursesConfig {
  title?: string;
  subtitle?: string;
  maxCourses: number;
  showPrice: boolean;
}

/* ─── Editor prop contract ─────────────────────────────────── */

export interface EditorProps<T> {
  config: T;
  onChange: (config: T) => void;
}

/* ─── Day meta (ordered) ───────────────────────────────────── */

export const DAYS_ORDERED: ReadonlyArray<{ key: DayKey; day: string; dayShort: string }> = [
  { key: 'monday', day: 'Montag', dayShort: 'Mo' },
  { key: 'tuesday', day: 'Dienstag', dayShort: 'Di' },
  { key: 'wednesday', day: 'Mittwoch', dayShort: 'Mi' },
  { key: 'thursday', day: 'Donnerstag', dayShort: 'Do' },
  { key: 'friday', day: 'Freitag', dayShort: 'Fr' },
  { key: 'saturday', day: 'Samstag', dayShort: 'Sa' },
  { key: 'sunday', day: 'Sonntag', dayShort: 'So' },
];
